import { Dispatch, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/legacy/image';
import { BaseResponseData, CommonItemProps, ModalProps, PurchaseInstance, SimpleItem, ProductInstanceOrderData } from 'types';
import { postPurchasing, postCart, postExternalCartLink } from 'api';
import { formatServerErrorsForNotification } from 'utils/services';
import { getOfferErrorMessage } from 'utils/validators';
import { SnackbarProps } from 'components/Snackbar';
import { Button } from 'components/Button';
import { DynamicForm } from 'components/DynamicForm';

import cx from 'classnames';
import styles from './ProductPage.module.scss';
import { GearVariantProps } from '../../components/GearVariants/GearVariants';
import { getDataFromLocalStore, setDataToLocalStore } from '../../utils/localStoreTools';
import order from '../../pages/admin-panel/sizes/order';

export type PageControlsProps = {
  brand: CommonItemProps;
  id: string;
  image: string;
  inCart: boolean;
  offerAllowed: boolean;
  isAuth: boolean;
  isSold?: boolean;
  price: string | number | undefined;
  minOffer?: number;
  redirectToAuth: () => void;
  setModalProps: Dispatch<ModalProps | null>;
  setSnackNotification: Dispatch<SnackbarProps | null>;
  sizes: SimpleItem[];
  title: string;
  type: 'sell' | 'rent' | 'sale';
  selectedVariant?: GearVariantProps;
  disabled: boolean;
  isOutOfStock: boolean;
  orderData?: null | ProductInstanceOrderData;
};

const getOfferModalFields = (price: number, minOffer: number) => [
  {
    type: 'text',
    fieldName: 'requested_price',
    placeholder: 'your price',
    isThin: true,
    prefix: '$',
    validator: (value: number) => getOfferErrorMessage(value, Math.ceil(price * minOffer / 100)),
    required: true,
  },
  {
    type: 'description',
    value: 'Make an offer you think is fair. Nobody likes a lowball, but what is it worth to you?!',
  },
];

export const PageControls = ({
  brand,
  id,
  image,
  isAuth,
  inCart,
  isSold,
  price,
  minOffer=20,
  redirectToAuth,
  sizes,
  setModalProps,
  setSnackNotification,
  title,
  type,
  selectedVariant,
  disabled,
  isOutOfStock,
  offerAllowed,
  orderData,
}: PageControlsProps) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isInCart, setIsInCart] = useState(inCart);
  const guestCartToken = isAuth ? null : getDataFromLocalStore('guestCartToken');

  const onOfferSend = (data: Record<string, string>) => {
    setModalProps(null);
    return handleOrderCreation(+data.requested_price);
  };

  const handleCart = () => {
    setModalProps(null);

    if (orderData?.external_cart) {
      return handleExternalCartLink();
    } else {
      if(isInCart)
        router.push(`/cart${guestCartToken ? `?guestCartKey=${guestCartToken}` : ''}`);
      else
        return handleAddToCart();
    }
  };

  const goToPurchasePage = (id: number, guestToken?: string) => {
    !!window.gtag && window.gtag('event', `${type}-create`, { event_category: 'order' })
    if (type === 'rent') {
      // need reload since google map api calls with different props for maps and autocomplete
      return window.location.assign(`/purchasing/${id}`);
    }
    return router.push(`/purchasing/${id}${guestToken ? `?guestToken=${guestToken}` : ''}`);
  };

  const handleOrderCreation = async (offerPrice?: number) => {
    if (isLoading) return;
    setIsLoading(true);
    const purchaseOrderResponse: BaseResponseData<PurchaseInstance> = await postPurchasing(id, 'create', {
      with_offer: !!offerPrice,
      requested_price: offerPrice || null,
      variant: selectedVariant?.id || undefined,
    });
    if (purchaseOrderResponse.data?.id) {
      goToPurchasePage(purchaseOrderResponse.data.id, purchaseOrderResponse.guestToken);
    } else {
      setSnackNotification(formatServerErrorsForNotification(purchaseOrderResponse));
    }
    // need timeout since buttons came active before redirect
    setTimeout(() => setIsLoading(false), 5000) ;
  };

  const handleAddToCart = async () => {
    if(isAuth)
      setDataToLocalStore('guestCartToken', null);
    if (isLoading) return;
    setIsLoading(true);
    const cartResponse: BaseResponseData<PurchaseInstance> = await postCart({
      item_id: id,
      item_variant_id: selectedVariant?.id || undefined,
    }, {}, guestCartToken);
    if (cartResponse.data?.cart_key) {
      if(!isAuth)
        setDataToLocalStore('guestCartToken', cartResponse.data?.cart_key);
      setIsInCart(true)
      // goToPurchasePage(purchaseOrderResponse.data.id, purchaseOrderResponse.guestToken);
    } else {
      setSnackNotification(formatServerErrorsForNotification(cartResponse));
    }
    setIsLoading(false)
  };

  const handleExternalCartLink = async () => {
    if (isLoading) return;
    setIsLoading(true);

    const response  = await postExternalCartLink(id, {
      variant: selectedVariant?.id || undefined,
    });

    // @ts-ignore
    if (response.link) {
      // @ts-ignore
      location.href = response.link;
    } else {
      setSnackNotification(formatServerErrorsForNotification(response));
      setIsLoading(false);
    }
  }

  const onOfferCancel = () => {
    setModalProps(null);
    // router.reload();
  };

  const handleOffer = () => {
    setModalProps({
      title: 'Make an Offer',
      withCrossIcon: true,
      onCoverClick: onOfferCancel,
      child: (
        <>
          {renderOfferItemInfo()}
          <div style={{ marginTop: '23px', position: 'relative', width: '100%', height: '1px' }} />
          <h4 style={{ margin: '0 0 8px', position: 'relative' }}>Offer Price</h4>
          <DynamicForm
            marginMode="none"
            fields={getOfferModalFields(+(price || 0), (minOffer || 20))}
            disableButton={isLoading}
            onSend={onOfferSend}
            onCancel={onOfferCancel}
            buttonsShape="slim"
            buttonCaption="Submit"
          />
        </>
      ),
    });
  };

  const renderOfferItemInfo = () => (
    <div className={styles.offerProductInfoContainer}>
      <div className={styles.offerProductImage}>
        <Image src={selectedVariant?.images[0]['400x400'] || image || '/'} layout="fill" objectFit="cover" alt={title} />
      </div>
      <div>
        <p className={styles.offerProductTitle}>{title}</p>
        <p className={styles.offerProductPrice}>${selectedVariant?.price || price}</p>
        <div className={styles.offerProductDetails}>
          {selectedVariant ? (
            <p>
              <span>{selectedVariant.options.map((o) => o.value).join(' / ')}</span>
            </p>
          ) : (
            <p>
              Size: <span>{sizes.map((size) => size.title).join(' ')}</span>
            </p>
          )}
          &nbsp;|&nbsp;
          <p>
            Brand: <span>{brand.title}</span>
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={cx(styles.gearInfoOfferControls, styles.topOffset)}>
      {isSold ? (
        <Button caption="Sold" disabled={true} action={() => null} />
      ) : (
        <>
          <Button
            className="buy-btn"
            action={(isAuth || type === 'sell') ? handleCart : redirectToAuth}
            caption={type !== 'rent' ? (isOutOfStock ? 'OUT OF STOCK' : (isInCart ? 'IN YOUR CART' : 'ADD TO CART')) : 'RENT'}
            disabled={isLoading || disabled}
          />
          {+(price || 0) >= 2 && type !== 'rent' && offerAllowed && !orderData?.external_cart && (
            <Button
              className="offer-btn"
              caption="MAKE AN OFFER"
              action={isAuth ? handleOffer : redirectToAuth}
              buttonType="white"
              disabled={isLoading || disabled}
            />
          )}
        </>
      )}
    </div>
  );
};
